import React, { useState, useMemo } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LinkIcon from '@mui/icons-material/LinkOutlined';

/**
 * Modal for linking an existing registry entry as a child of the current entry.
 */
const LinkChildModal = ({
  isOpen,
  onClose,
  onLink,
  theme,
  allEntries,
  currentEntry,   // the parent entry we're linking to
  currentChildren,// already-linked children (to exclude)
}) => {
  const [query, setQuery] = useState('');
  const [linking, setLinking] = useState(false);

  const candidates = useMemo(() => {
    if (!allEntries || !currentEntry) return [];
    const childIds = new Set((currentChildren || []).map(c => c.id));
    return allEntries.filter(e =>
      e.id !== currentEntry.id &&
      !childIds.has(e.id) &&
      (!query || e.name.toLowerCase().includes(query.toLowerCase()) || e.code.toLowerCase().includes(query.toLowerCase()))
    );
  }, [allEntries, currentEntry, currentChildren, query]);

  const handleLink = async (entry) => {
    setLinking(true);
    try {
      await onLink(entry);
      onClose();
    } finally {
      setLinking(false);
    }
  };

  if (!isOpen) return null;

  const isDark = theme.name === 'dark';
  const overlay = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 5500,
  };
  const modal = {
    background: isDark ? '#1a1f2e' : '#ffffff',
    borderRadius: 12, padding: 24,
    width: 'min(480px, 92vw)', maxHeight: '80vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
    border: `1px solid ${theme.toolbar.buttonBorder}`,
  };
  const input = {
    width: '100%', padding: '8px 10px', borderRadius: 6,
    border: `1px solid ${theme.toolbar.buttonBorder}`,
    background: theme.toolbar.buttonBackground,
    color: theme.toolbar.text, fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };
  const rowStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 10px', borderRadius: 6, cursor: linking ? 'not-allowed' : 'pointer',
    border: `1px solid ${theme.toolbar.buttonBorder}`,
    background: theme.toolbar.buttonBackground,
    color: theme.toolbar.text,
    opacity: linking ? 0.6 : 1,
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 700, color: theme.toolbar.text, marginBottom: 16 }}>
          Привязать существующую запись
        </div>
        <div style={{ fontSize: 13, opacity: 0.7, color: theme.toolbar.text, marginBottom: 12 }}>
          Выбранная запись станет дочерней к <strong>{currentEntry?.name}</strong>
        </div>

        <input
          style={{ ...input, marginBottom: 12 }}
          placeholder="Поиск по названию или коду..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          autoFocus
        />

        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {candidates.length === 0 && (
            <div style={{ fontSize: 13, opacity: 0.6, color: theme.toolbar.text, textAlign: 'center', padding: 20 }}>
              Нет доступных записей
            </div>
          )}
          {candidates.map(entry => (
            <div
              key={entry.id}
              style={rowStyle}
              onClick={() => !linking && handleLink(entry)}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <LinkIcon fontSize="small" style={{ opacity: 0.5 }} />
                  {entry.name}
                </div>
                <div style={{ fontSize: 11, opacity: 0.6 }}>{entry.code}{entry.kind_slug ? ` · ${entry.kind_slug}` : ''}</div>
              </div>
              <span style={{
                fontSize: 11, padding: '2px 8px', borderRadius: 4,
                background: isDark ? '#1e3a5f' : '#dbeafe',
                color: isDark ? '#60a5fa' : '#1e40af',
              }}>Привязать</span>
            </div>
          ))}
        </div>

        <button
          onClick={onClose}
          style={{
            marginTop: 16, padding: '8px 16px', borderRadius: 6, fontSize: 14,
            fontWeight: 600, cursor: 'pointer',
            background: theme.toolbar.buttonBackground,
            color: theme.toolbar.text,
            border: `1px solid ${theme.toolbar.buttonBorder}`,
          }}
        >
          Отмена
        </button>
      </div>
    </div>
  );
};

export default LinkChildModal;
